import { ChangeEvent, DragEvent as ReactDragEvent, FC, PointerEvent as ReactPointerEvent, ReactNode, RefObject, useEffect, useLayoutEffect, useRef, useState } from "react";
import { driver, type DriveStep, type Driver as DriverJs } from "driver.js";
import "driver.js/dist/driver.css";
import { Fonts } from "./Fonts";
import { OfuseIcon } from "./OfuseIcon";
import { LuCalendar, LuImagePlus, LuCalendarRange, LuMessageSquare, LuDownload, LuCopy, LuX, LuCheck, LuPencil, LuLayers, LuPalette, LuTrash2, LuShare2, LuLightbulb, LuImageUpscale, LuImages, LuPanelsTopLeft, LuInfo, LuContactRound, LuHistory, LuCircleHelp } from "react-icons/lu";
import { APP_TOUR_STEPS } from "../data/appTourSteps";
import { DEFAULT_TEMPLATE, DEFAULT_THEME, resolveTemplateThemeSelection, TEMPLATES } from "../styles/templates";
import { UPDATE_HISTORY_ITEMS } from "../data/updateHistory";

type ScheduleItem = {
  time: string;
  content: string;
  isOff: boolean;
};

type DayInfo = {
  dayNumber: number;
  weekLabel: string;
  dateLabel: string;
};

type DragTarget = "mainImage" | "credit" | "comment" | "logo";

type Point = {
  x: number;
  y: number;
};

type DragPositions = Record<DragTarget, Point>;
type ResizableTarget = "mainImage" | "logo";
type ElementSizes = Record<ResizableTarget, number>;
type ResizeHandle = "nw" | "n" | "ne" | "w" | "center" | "e" | "sw" | "s" | "se";
type DraftState = {
  schemaVersion: 1;
  updatedAt: string;
  startDate: string;
  schedules: ScheduleItem[];
  offDayLabel: string;
  credit: string;
  commentTitle: string;
  commentBody: string;
  attentionText: string;
  dragPositions: DragPositions;
  elementSizes: ElementSizes;
  selectedTemplateId: string;
  selectedThemeId: string;
};

type Html2CanvasOptions = {
  backgroundColor?: null | string;
  scale?: number;
  useCORS?: boolean;
};

type Html2CanvasFn = (element: HTMLElement, options?: Html2CanvasOptions) => Promise<HTMLCanvasElement>;

declare global {
  interface Window {
    html2canvas?: Html2CanvasFn;
  }
}

const EXPORT_WIDTH = 1200;
const LG_BREAKPOINT = 1024;
const DRAFT_STORAGE_KEY = "schedule-tool:draft";
const LOGO_STORAGE_KEY = "schedule-tool:logo-image";
const MAIN_IMAGE_STORAGE_KEY = "schedule-tool:main-image";
type FaqItem = {
  q: string;
  a: ReactNode;
};
const WEEK_LABEL_SETS = {
  jaShort: ["月", "火", "水", "木", "金", "土", "日" ],
  enShort: ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"],
  enLong: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
} as const;
const DEFAULT_ATTENTION = "※予定は変更になる場合があります。";
const ABOUT_FAQ: FaqItem[] = [
  {
    q: "無料で使えますか？",
    a: (
      <>
      はい、無料でご利用いただけます。<br />商用・非商用問わずお使いください。
      </>
    ),
  },
  {
    q: "今後、有料になることはありますか？",
    a: (
      <>
      今後、機能拡張があってもすべての機能を無料で提供いたします。
      </>
    ),
  },
  {
    q: "スマートフォンでも使えますか？",
    a: (
      <>
      一応ご利用いただけますが、PC・タブレットでのご利用を推奨しています。<br />スマートフォンでは一部操作がしづらい場合があります。
      </>
    ),
  },
  {
    q: "入力したテキストや画像は開発者に見られますか？",
    a: (
      <>
      いいえ。入力データはすべてお使いのブラウザ上でのみ処理されており、<br />どこかに送信されたり保存されたりすることはありません。
      </>
    ),
  },
  {
    q: "使ったことを報告する必要はありますか？",
    a: (
      <>
      不要です。報告もクレジット表記も必要ありません。<br />ただ、お知らせいただけると開発者がとても喜びます。→ <a href="https://x.com/torica124nishi" target="_blank" rel="noopener noreferrer"  className="text-red-500 underline hover:opacity-60">X(Twitter)</a>
      </>
    ),
  },
  {
    q: "どのくらいの頻度で更新されますか？",
    a: (
      <>
      不定期です。気が向いたら更新しています。<br />アップデートの際は <a href="https://x.com/torica124nishi" target="_blank" rel="noopener noreferrer"  className="text-red-500 underline hover:opacity-60">X(Twitter)</a> でお知らせする予定です。
      </>
    ),
  },
  {
    q: "不具合を見つけました。",
    a: (
      <>
      申し訳ありません。 <a href="https://docs.google.com/forms/d/e/1FAIpQLScDnLogiT0Z0ReGkJWr1DpuoIjPOmPhbkC1PaexmEBcPcETZA/viewform?usp=publish-editor" target="_blank" rel="noopener noreferrer"  className="text-red-500 underline hover:opacity-60">Googleフォーム</a> からご報告いただけると助かります。<br />できる限り対応いたします。
      </>
    ),
  },
  {
    q: "こんな機能がほしいです！",
    a: (
      <>
      ぜひ教えてください。不具合報告と同様、<a href="https://docs.google.com/forms/d/e/1FAIpQLScDnLogiT0Z0ReGkJWr1DpuoIjPOmPhbkC1PaexmEBcPcETZA/viewform?usp=publish-editor" target="_blank" rel="noopener noreferrer"  className="text-red-500 underline hover:opacity-60">Googleフォーム</a> からどうぞ。<br />すべてのご要望にお応えできるとは限りませんが、参考にさせていただきます。
      </>
    ),
  },
  {
    q: "開発を応援したいです！",
    a: (
      <>
      ありがとうございます。<br />「はいすけ」をSNSなどで紹介していただけるだけでも、とても嬉しいです。<br />ファンレターやご支援を送りたい方は、 <a href="https://ofuse.me/torica124nishi/letter" target="_blank" rel="noopener noreferrer"  className="text-red-500 underline hover:opacity-60">OFUSE</a> もご利用いただけます。
      </>
    ),
  },
];
const EMPTY_SCHEDULE_CONTENT = "配信内容を記入";
const OFF_DAY_LABEL = "配信おやすみ";
const EXPORT_FILE_NAME = "stream_schedule.png";
const SHARE_TITLE = "配信スケジュール画像";
const IMAGE_FILE_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/webp"] as const;
const IMAGE_FILE_ACCEPT = IMAGE_FILE_TYPES.join(", ");
const IMAGE_FILE_EXTENSION_PATTERN = /\.(png|jpe?g|webp)$/i;
const IMAGE_FILE_ERROR_MESSAGE = "PNG / JPEG / WEBP 形式の画像を選択してください。";
const DEFAULT_DRAG_POSITIONS: DragPositions = {
  mainImage: { x: 0, y: 0 },
  credit: { x: 50, y: -410 },
  comment: { x: 50, y: 280 },
  logo: { x: 20, y: -480 },
};
const DEFAULT_ELEMENT_SIZES: ElementSizes = {
  mainImage: 480,
  logo: 360,
};
const MIN_ELEMENT_SIZES: ElementSizes = {
  mainImage: 220,
  logo: 140,
};
// テンプレートによって空きスペースの位置が大きく変わるため、切り替え時の初期配置を持たせる。
// 未登録のテンプレートは DEFAULT_DRAG_POSITIONS / DEFAULT_ELEMENT_SIZES を使う。
const TEMPLATE_DRAG_POSITIONS: Record<string, DragPositions> = {
  board: {
    mainImage: { x: 0, y: 0 },
    credit: { x: 0, y: 0 },
    comment: { x: 0, y: 0 },
    logo: { x: 0, y: 0 },
  },
};
const TEMPLATE_ELEMENT_SIZES: Record<string, ElementSizes> = {
  board: {
    mainImage: 400,
    logo: 300,
  },
};

const getTemplateDragPositions = (templateId: string) =>
  TEMPLATE_DRAG_POSITIONS[templateId] ?? DEFAULT_DRAG_POSITIONS;

const getTemplateElementSizes = (templateId: string) =>
  TEMPLATE_ELEMENT_SIZES[templateId] ?? DEFAULT_ELEMENT_SIZES;
const RESIZE_HANDLES: ResizeHandle[] = ["nw", "n", "ne", "w", "center", "e", "sw", "s", "se"];
const DEFAULT_COMMENT_TITLE = "今週のひとこと";
const MAX_VISIBLE_UPDATE_HISTORY = 2;

const createEmptySchedules = (): ScheduleItem[] =>
  Array.from({ length: 7 }, () => ({
    time: "",
    content: "",
    isOff: false,
  }));

const resolveOffDayLabel = (value: string) => value.trim() || OFF_DAY_LABEL;

const getScheduleDisplayValues = (item: ScheduleItem, offDayLabel: string) => ({
  time: item.isOff ? "" : item.time,
  content: item.isOff ? resolveOffDayLabel(offDayLabel) : item.content || EMPTY_SCHEDULE_CONTENT,
});

const getPixelValue = (value: string) => {
  const parsed = Number.parseFloat(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const resizeScheduleContentTextarea = (textarea: HTMLTextAreaElement | null) => {
  if (!textarea) {
    return;
  }

  const computedStyle = window.getComputedStyle(textarea);
  const fontSize = getPixelValue(computedStyle.fontSize) || 14;
  const lineHeight = getPixelValue(computedStyle.lineHeight) || fontSize * 1.375;
  const paddingY = getPixelValue(computedStyle.paddingTop) + getPixelValue(computedStyle.paddingBottom);
  const minHeight = Math.ceil(lineHeight + paddingY);
  const maxHeight = Math.ceil(lineHeight * 2 + paddingY);

  textarea.style.height = "auto";
  const nextHeight = Math.min(Math.max(textarea.scrollHeight, minHeight), maxHeight);
  textarea.style.height = `${nextHeight}px`;
  textarea.style.overflowY = textarea.scrollHeight > maxHeight ? "auto" : "hidden";
};

const formatDateValue = (date: Date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const normalizeDateToMonday = (date: Date) => {
  const monday = new Date(date);
  const day = monday.getDay();
  const diffFromMonday = (day + 6) % 7;
  monday.setDate(monday.getDate() - diffFromMonday);
  return monday;
};

const normalizeStartDateValue = (startDate: string) => {
  const baseDate = startDate ? new Date(`${startDate}T00:00:00`) : new Date();
  return formatDateValue(normalizeDateToMonday(baseDate));
};

const buildAppTourSteps = () =>
  APP_TOUR_STEPS.reduce<DriveStep[]>((steps, step) => {
    const element = document.querySelector(step.selector);
    if (!element) {
      return steps;
    }

    steps.push({
      element,
      popover: {
        title: step.title,
        description: step.description,
        side: step.side,
        align: step.align,
      },
    });

    return steps;
  }, []);

const buildWeek = (startDate: string) => {
  const baseDate = new Date(`${normalizeStartDateValue(startDate)}T00:00:00`);
  const days: DayInfo[] = [];

  for (let index = 0; index < 7; index += 1) {
    const current = new Date(baseDate);
    current.setDate(baseDate.getDate() + index);
    days.push({
      dayNumber: current.getDate(),
      weekLabel: WEEK_LABEL_SETS.jaShort[index],
      dateLabel: formatDateValue(current),
    });
  }

  const first = days[0];
  const lastDate = new Date(`${days[6].dateLabel}T00:00:00`);
  const startMonth = baseDate.getMonth() + 1;
  const endMonth = lastDate.getMonth() + 1;
  const periodStartLabel = `${startMonth}/${first.dayNumber}`;
  const periodEndLabel = `${endMonth}/${lastDate.getDate()}`;

  return { days, periodStartLabel, periodEndLabel };
};

const readFileAsDataUrl = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : "");
    reader.onerror = () => reject(new Error(`Failed to read file: ${file.name}`));
    reader.readAsDataURL(file);
  });

const isSupportedImageFile = (file: File) =>
  IMAGE_FILE_TYPES.includes(file.type as (typeof IMAGE_FILE_TYPES)[number]) || IMAGE_FILE_EXTENSION_PATTERN.test(file.name);

const getFirstSupportedImageFile = (files: FileList | File[] | null | undefined) => {
  const droppedFiles = files ? Array.from(files) : [];
  return droppedFiles.find((file) => isSupportedImageFile(file)) ?? null;
};

const hasFileDragData = (dataTransfer: DataTransfer | null) => Array.from(dataTransfer?.types ?? []).includes("Files");

export const createFileFromDataUrl = (dataUrl: string, fileName = EXPORT_FILE_NAME) => {
  const [header, payload] = dataUrl.split(",", 2);
  if (!header || !payload) {
    throw new Error("Invalid data URL");
  }

  const mimeType = /^data:(.*?)(;base64)?$/i.exec(header)?.[1] || "application/octet-stream";
  const decoded = header.includes(";base64") ? atob(payload) : decodeURIComponent(payload);
  const bytes = new Uint8Array(decoded.length);

  for (let index = 0; index < decoded.length; index += 1) {
    bytes[index] = decoded.charCodeAt(index);
  }

  return new File([bytes], fileName, { type: mimeType });
};

type WebShareNavigator = Pick<Navigator, "share"> & Partial<Pick<Navigator, "canShare">>;
type ImageClipboard = Pick<Clipboard, "write">;

export const buildScheduleShareText = (periodStartLabel: string, periodEndLabel: string) =>
  `${periodStartLabel}-${periodEndLabel}の配信スケジュールです。`;

export const copyImageDataUrlToClipboard = async ({
  dataUrl,
  clipboardLike,
  clipboardItemCtor,
}: {
  dataUrl: string;
  clipboardLike?: ImageClipboard;
  clipboardItemCtor?: typeof ClipboardItem;
}) => {
  if (!clipboardLike || typeof clipboardLike.write !== "function" || typeof clipboardItemCtor === "undefined") {
    return "unsupported" as const;
  }

  const file = createFileFromDataUrl(dataUrl);
  await clipboardLike.write([new clipboardItemCtor({ [file.type || "image/png"]: file })]);
  return "copied" as const;
};

const canShareImageFile = (navigatorLike: WebShareNavigator, file: File) => {
  if (typeof navigatorLike.share !== "function") {
    return false;
  }

  if (typeof navigatorLike.canShare === "function") {
    return navigatorLike.canShare({ files: [file] });
  }

  return true;
};

export const shareScheduleImage = async ({
  dataUrl,
  periodStartLabel,
  periodEndLabel,
  navigatorLike,
}: {
  dataUrl: string;
  periodStartLabel: string;
  periodEndLabel: string;
  navigatorLike: WebShareNavigator;
}) => {
  const file = createFileFromDataUrl(dataUrl);
  if (!canShareImageFile(navigatorLike, file)) {
    return "unsupported" as const;
  }

  await navigatorLike.share({
    files: [file],
    title: SHARE_TITLE,
    text: buildScheduleShareText(periodStartLabel, periodEndLabel),
  });

  return "shared" as const;
};

const waitForFonts = async () => {
  if ("fonts" in document) {
    await (document as Document & { fonts: FontFaceSet }).fonts.ready;
  }

  await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()));
};

const waitForElementImages = async (element: HTMLElement) => {
  const images = Array.from(element.querySelectorAll("img")).filter((image) => image.getAttribute("src"));
  await Promise.all(
    images.map(
      (image) =>
        image.complete
          ? Promise.resolve()
          : new Promise<void>((resolve) => {
              const handleDone = () => {
                image.removeEventListener("load", handleDone);
                image.removeEventListener("error", handleDone);
                resolve();
              };

              image.addEventListener("load", handleDone);
              image.addEventListener("error", handleDone);
            }),
    ),
  );
};

const renderElementToDataUrl = async (element: HTMLElement) => {
  await waitForFonts();
  await waitForElementImages(element);

  if (!window.html2canvas) {
    throw new Error("html2canvas is not available");
  }

  const canvas = await window.html2canvas(element, {
    backgroundColor: null,
    scale: 1,
    useCORS: true,
  });

  return canvas.toDataURL("image/png");
};

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

const normalizeDraftPoint = (value: unknown, fallback: Point): Point => {
  if (!isRecord(value)) {
    return fallback;
  }

  const x = typeof value.x === "number" && Number.isFinite(value.x) ? value.x : fallback.x;
  const y = typeof value.y === "number" && Number.isFinite(value.y) ? value.y : fallback.y;
  return { x, y };
};

const normalizeDraftScheduleItem = (value: unknown): ScheduleItem => {
  if (!isRecord(value)) {
    return { time: "", content: "", isOff: false };
  }

  return {
    time: typeof value.time === "string" ? value.time : "",
    content: typeof value.content === "string" ? value.content : "",
    isOff: typeof value.isOff === "boolean" ? value.isOff : false,
  };
};

const normalizeDraftSchedules = (value: unknown): ScheduleItem[] => {
  const items = Array.isArray(value) ? value : [];

  return Array.from({ length: 7 }, (_, index) => normalizeDraftScheduleItem(items[index]));
};

const normalizeDraftElementSize = (value: unknown, fallback: number, min: number) => {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return fallback;
  }

  return Math.max(min, Math.round(value));
};

const normalizeDraftSelection = (templateValue: unknown, themeValue: unknown) =>
  resolveTemplateThemeSelection({
    templateId: typeof templateValue === "string" ? templateValue : undefined,
    themeId: typeof themeValue === "string" ? themeValue : undefined,
  });

const parseDraftState = (value: string): DraftState | null => {
  try {
    const parsed: unknown = JSON.parse(value);
    if (!isRecord(parsed)) {
      return null;
    }

    const startDate = typeof parsed.startDate === "string" ? normalizeStartDateValue(parsed.startDate) : normalizeStartDateValue("");
    const offDayLabel = typeof parsed.offDayLabel === "string" ? parsed.offDayLabel : "";
    const credit = typeof parsed.credit === "string" ? parsed.credit : "";
    const commentTitle = typeof parsed.commentTitle === "string" ? parsed.commentTitle : DEFAULT_COMMENT_TITLE;
    const commentBody = typeof parsed.commentBody === "string" ? parsed.commentBody : "";
    const attentionText = typeof parsed.attentionText === "string" ? parsed.attentionText : DEFAULT_ATTENTION;
    const selection = normalizeDraftSelection(parsed.selectedTemplateId, parsed.selectedThemeId);
    const dragPositionsValue = isRecord(parsed.dragPositions) ? parsed.dragPositions : {};
    const elementSizesValue = isRecord(parsed.elementSizes) ? parsed.elementSizes : {};
    // 既定値は必ず保存されていたテンプレートのものを使う（テンプレートごとに基準位置が違うため）
    const fallbackDragPositions = getTemplateDragPositions(selection.templateId);
    const fallbackElementSizes = getTemplateElementSizes(selection.templateId);

    return {
      schemaVersion: 1,
      updatedAt: typeof parsed.updatedAt === "string" ? parsed.updatedAt : "",
      startDate,
      schedules: normalizeDraftSchedules(parsed.schedules),
      offDayLabel,
      credit,
      commentTitle,
      commentBody,
      attentionText,
      dragPositions: {
        mainImage: normalizeDraftPoint(dragPositionsValue.mainImage, fallbackDragPositions.mainImage),
        credit: normalizeDraftPoint(dragPositionsValue.credit, fallbackDragPositions.credit),
        comment: normalizeDraftPoint(dragPositionsValue.comment, fallbackDragPositions.comment),
        logo: normalizeDraftPoint(dragPositionsValue.logo, fallbackDragPositions.logo),
      },
      elementSizes: {
        mainImage: normalizeDraftElementSize(
          elementSizesValue.mainImage,
          fallbackElementSizes.mainImage,
          MIN_ELEMENT_SIZES.mainImage,
        ),
        logo: normalizeDraftElementSize(
          elementSizesValue.logo,
          fallbackElementSizes.logo,
          MIN_ELEMENT_SIZES.logo,
        ),
      },
      selectedTemplateId: selection.templateId,
      selectedThemeId: selection.themeId,
    };
  } catch (error) {
    console.error(error);
    return null;
  }
};

export const App: FC = () => {
  const [startDate, setStartDate] = useState(() => normalizeStartDateValue(""));
  const [credit, setCredit] = useState("");
  const [commentTitle, setCommentTitle] = useState(DEFAULT_COMMENT_TITLE);
  const [commentBody, setCommentBody] = useState("");
  const [attentionText, setAttentionText] = useState(DEFAULT_ATTENTION);
  const [schedules, setSchedules] = useState<ScheduleItem[]>(createEmptySchedules);
  const [offDayLabel, setOffDayLabel] = useState("");
  const [logoImageUrl, setLogoImageUrl] = useState("");
  const [mainImageUrl, setMainImageUrl] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState(DEFAULT_TEMPLATE.id);
  const [selectedThemeId, setSelectedThemeId] = useState(DEFAULT_THEME.id);
  const [dragPositions, setDragPositions] = useState(DEFAULT_DRAG_POSITIONS);
  const [elementSizes, setElementSizes] = useState(DEFAULT_ELEMENT_SIZES);
  const [activeTarget, setActiveTarget] = useState<DragTarget | null>(null);
  const [previewScale, setPreviewScale] = useState(1);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isAboutModalOpen, setIsAboutModalOpen] = useState(false);
  const [isUpdateHistoryModalOpen, setIsUpdateHistoryModalOpen] = useState(false);
  const [exportImageUrl, setExportImageUrl] = useState("");
  const [isExporting, setIsExporting] = useState(false);
  const [copyMessage, setCopyMessage] = useState("");
  const [isDraftLoaded, setIsDraftLoaded] = useState(false);
  const [activeImageDropTarget, setActiveImageDropTarget] = useState<ResizableTarget | null>(null);
  const [isMobileLayout, setIsMobileLayout] = useState(() => typeof window !== "undefined" && window.innerWidth < LG_BREAKPOINT);
  const previewAreaRef = useRef<HTMLDivElement>(null);
  const exportContentRef = useRef<HTMLDivElement>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const mainImageInputRef = useRef<HTMLInputElement>(null);
  const scheduleContentRefs = useRef<Array<HTMLTextAreaElement | null>>([]);
  const tutorialDriverRef = useRef<DriverJs | null>(null);
  const imageDragDepthRef = useRef<Record<ResizableTarget, number>>({
    mainImage: 0,
    logo: 0,
  });
  const interactionRef = useRef<{
    mode: "drag" | "resize";
    target: DragTarget;
    handle?: ResizeHandle;
    pointerId: number;
    startClient: Point;
    startPosition: Point;
    startSize?: number;
  } | null>(null);

  const { days, periodStartLabel, periodEndLabel } = buildWeek(startDate);
  const { template, theme } = resolveTemplateThemeSelection({ templateId: selectedTemplateId, themeId: selectedThemeId });
  // 配置のリセットは常に「今のテンプレートの初期値」に戻す。
  // DEFAULT_* を直接使うと、ボードのように基準位置が違うテンプレートで配置が飛ぶ。
  const templateDragPositions = getTemplateDragPositions(template.id);
  const templateElementSizes = getTemplateElementSizes(template.id);
  const TemplateBackgroundDecoration = template.BackgroundDecoration;
  const TemplateLabelTitle = template.LabelTitle;
  const TemplateScheduleTable = template.ScheduleTable;

  useEffect(() => {
    const updateLayoutMode = () => {
      setIsMobileLayout(window.innerWidth < LG_BREAKPOINT);
    };

    updateLayoutMode();
    window.addEventListener("resize", updateLayoutMode);
    return () => window.removeEventListener("resize", updateLayoutMode);
  }, []);

  useEffect(() => {
    return () => {
      tutorialDriverRef.current?.destroy();
      tutorialDriverRef.current = null;
    };
  }, []);

  useLayoutEffect(() => {
    const resizePreview = () => {
      const previewArea = previewAreaRef.current;
      if (!previewArea) {
        return;
      }

      setPreviewScale(previewArea.offsetWidth / EXPORT_WIDTH);
    };

    resizePreview();
    window.addEventListener("resize", resizePreview);
    return () => window.removeEventListener("resize", resizePreview);
  }, [isMobileLayout]);

  useLayoutEffect(() => {
    scheduleContentRefs.current.forEach((textarea) => resizeScheduleContentTextarea(textarea));
  }, [schedules]);

  useEffect(() => {
    try {
      const savedDraft = window.localStorage.getItem(DRAFT_STORAGE_KEY);
      const savedLogo = window.localStorage.getItem(LOGO_STORAGE_KEY);
      const savedMainImage = window.localStorage.getItem(MAIN_IMAGE_STORAGE_KEY);
      const draft = savedDraft ? parseDraftState(savedDraft) : null;

      if (draft) {
        setStartDate(draft.startDate);
        setSchedules(draft.schedules);
        setOffDayLabel(draft.offDayLabel);
        setCredit(draft.credit);
        setCommentTitle(draft.commentTitle);
        setCommentBody(draft.commentBody);
        setAttentionText(draft.attentionText);
        setDragPositions(draft.dragPositions);
        setElementSizes(draft.elementSizes);
        setSelectedTemplateId(draft.selectedTemplateId);
        setSelectedThemeId(draft.selectedThemeId);
      }

      if (savedLogo) {
        setLogoImageUrl(savedLogo);
      }
      if (savedMainImage) {
        setMainImageUrl(savedMainImage);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsDraftLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (!isDraftLoaded) {
      return;
    }

    const draft: DraftState = {
      schemaVersion: 1,
      updatedAt: new Date().toISOString(),
      startDate,
      schedules,
      offDayLabel,
      credit,
      commentTitle,
      commentBody,
      attentionText,
      dragPositions,
      elementSizes,
      selectedTemplateId,
      selectedThemeId,
    };

    try {
      window.localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draft));
    } catch (error) {
      console.error(error);
    }
  }, [attentionText, commentBody, commentTitle, credit, dragPositions, elementSizes, isDraftLoaded, offDayLabel, schedules, selectedTemplateId, selectedThemeId, startDate]);

  useEffect(() => {
    if (!isDraftLoaded) {
      return;
    }

    try {
      if (logoImageUrl) {
        window.localStorage.setItem(LOGO_STORAGE_KEY, logoImageUrl);
      } else {
        window.localStorage.removeItem(LOGO_STORAGE_KEY);
      }
    } catch (error) {
      console.error(error);
    }
  }, [isDraftLoaded, logoImageUrl]);

  useEffect(() => {
    if (!isDraftLoaded) {
      return;
    }

    try {
      if (mainImageUrl) {
        window.localStorage.setItem(MAIN_IMAGE_STORAGE_KEY, mainImageUrl);
      } else {
        window.localStorage.removeItem(MAIN_IMAGE_STORAGE_KEY);
      }
    } catch (error) {
      console.error(error);
    }
  }, [isDraftLoaded, mainImageUrl]);

  useEffect(() => {
    if (activeTarget === "mainImage" && !mainImageUrl) {
      setActiveTarget(null);
    }
    if (activeTarget === "logo" && !logoImageUrl) {
      setActiveTarget(null);
    }
    if (activeTarget === "credit" && !credit) {
      setActiveTarget(null);
    }
  }, [activeTarget, credit, logoImageUrl, mainImageUrl]);

  useEffect(() => {
    const handlePointerMove = (event: PointerEvent) => {
      const interaction = interactionRef.current;
      if (!interaction) {
        return;
      }

      const dx = (event.clientX - interaction.startClient.x) / (previewScale || 1);
      const dy = (event.clientY - interaction.startClient.y) / (previewScale || 1);

      if (interaction.mode === "drag") {
        setDragPositions((current) => ({
          ...current,
          [interaction.target]: {
            x: interaction.startPosition.x + dx,
            y: interaction.startPosition.y + dy,
          },
        }));
        return;
      }

      if ((interaction.target === "mainImage" || interaction.target === "logo") && interaction.startSize) {
        const resizeTarget: ResizableTarget = interaction.target;
        const handle = interaction.handle;
        let delta = 0;
        let axisCount = 0;

        if (handle?.includes("e")) {
          delta += dx;
          axisCount += 1;
        }
        if (handle?.includes("w")) {
          delta += -dx;
          axisCount += 1;
        }
        if (handle?.includes("s")) {
          delta += dy;
          axisCount += 1;
        }
        if (handle?.includes("n")) {
          delta += -dy;
          axisCount += 1;
        }

        if (axisCount === 0) {
          delta = dx;
        } else {
          delta /= axisCount;
        }

        setElementSizes((current) => ({
          ...current,
          [resizeTarget]: Math.max(MIN_ELEMENT_SIZES[resizeTarget], Math.round(interaction.startSize! + delta)),
        }));
      }
    };

    const handlePointerUp = (event: PointerEvent) => {
      const interaction = interactionRef.current;
      if (!interaction || interaction.pointerId !== event.pointerId) {
        return;
      }

      interactionRef.current = null;
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };
  }, [previewScale]);

  const beginDrag = (target: DragTarget) => (event: ReactPointerEvent<HTMLElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    setActiveTarget(target);
    interactionRef.current = {
      mode: "drag",
      target,
      handle: "center",
      pointerId: event.pointerId,
      startClient: { x: event.clientX, y: event.clientY },
      startPosition: dragPositions[target],
    };
  };

  const beginResize = (target: ResizableTarget, handle: ResizeHandle) => (event: ReactPointerEvent<HTMLElement>) => {
    event.stopPropagation();
    if (handle === "center") {
      beginDrag(target)(event);
      return;
    }
    event.currentTarget.setPointerCapture(event.pointerId);
    setActiveTarget(target);
    interactionRef.current = {
      mode: "resize",
      target,
      handle,
      pointerId: event.pointerId,
      startClient: { x: event.clientX, y: event.clientY },
      startPosition: dragPositions[target],
      startSize: elementSizes[target],
    };
  };

  const renderResizeHandles = (target: ResizableTarget) =>
    RESIZE_HANDLES.map((handle) => (
      <button
        key={handle}
        type="button"
        className={`resize-handle resize-handle-${handle}`}
        onPointerDown={beginResize(target, handle)}
        aria-label={handle === "center" ? "要素を移動" : "要素を拡縮"}
      />
    ));

  const handleScheduleTextChange =
    (index: number, field: "time" | "content") =>
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = event.target.value;
      setSchedules((current) => current.map((item, itemIndex) => (itemIndex === index ? { ...item, [field]: value } : item)));
    };

  const handleScheduleTimeFocus = (index: number) => () => {
    setSchedules((current) =>
      current.map((item, itemIndex) => {
        if (itemIndex !== index || item.isOff || item.time) {
          return item;
        }

        return { ...item, time: "00:00" };
      }),
    );
  };

  const handleScheduleOffChange = (index: number) => (event: ChangeEvent<HTMLInputElement>) => {
    const isOff = event.target.checked;
    setSchedules((current) => current.map((item, itemIndex) => (itemIndex === index ? { ...item, isOff } : item)));
  };

  const resetImageInputValue = (inputRef: RefObject<HTMLInputElement>) => {
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const applyImageFiles = async ({
    files,
    setter,
    inputRef,
  }: {
    files: FileList | File[] | null | undefined;
    setter: (value: string) => void;
    inputRef: RefObject<HTMLInputElement>;
  }) => {
    const droppedFiles = files ? Array.from(files) : [];
    if (droppedFiles.length === 0) {
      return;
    }

    const file = getFirstSupportedImageFile(droppedFiles);
    if (!file) {
      resetImageInputValue(inputRef);
      window.alert(IMAGE_FILE_ERROR_MESSAGE);
      return;
    }

    try {
      setter(await readFileAsDataUrl(file));
    } catch (error) {
      console.error(error);
      window.alert("画像の読み込みに失敗しました。");
    } finally {
      resetImageInputValue(inputRef);
    }
  };

  const handleImageChange =
    (setter: (value: string) => void, inputRef: RefObject<HTMLInputElement>) => async (event: ChangeEvent<HTMLInputElement>) => {
      await applyImageFiles({
        files: event.target.files,
        setter,
        inputRef,
      });
    };

  const handleImageDragEnter = (target: ResizableTarget) => (event: ReactDragEvent<HTMLDivElement>) => {
    if (!hasFileDragData(event.dataTransfer)) {
      return;
    }

    event.preventDefault();
    imageDragDepthRef.current[target] += 1;
    setActiveImageDropTarget(target);
  };

  const handleImageDragOver = (target: ResizableTarget) => (event: ReactDragEvent<HTMLDivElement>) => {
    if (!hasFileDragData(event.dataTransfer)) {
      return;
    }

    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
    if (activeImageDropTarget !== target) {
      setActiveImageDropTarget(target);
    }
  };

  const handleImageDragLeave = (target: ResizableTarget) => (event: ReactDragEvent<HTMLDivElement>) => {
    if (!hasFileDragData(event.dataTransfer)) {
      return;
    }

    event.preventDefault();
    imageDragDepthRef.current[target] = Math.max(0, imageDragDepthRef.current[target] - 1);
    if (imageDragDepthRef.current[target] === 0) {
      setActiveImageDropTarget((current) => (current === target ? null : current));
    }
  };

  const handleImageDrop =
    (target: ResizableTarget, setter: (value: string) => void, inputRef: RefObject<HTMLInputElement>) =>
    async (event: ReactDragEvent<HTMLDivElement>) => {
      if (!hasFileDragData(event.dataTransfer)) {
        return;
      }

      event.preventDefault();
      imageDragDepthRef.current[target] = 0;
      setActiveImageDropTarget((current) => (current === target ? null : current));
      await applyImageFiles({
        files: event.dataTransfer.files,
        setter,
        inputRef,
      });
    };

  const handleClearImage = (target: "logo" | "mainImage") => () => {
    if (target === "logo") {
      setLogoImageUrl("");
      if (logoInputRef.current) {
        logoInputRef.current.value = "";
      }
      return;
    }

    setMainImageUrl("");
    if (mainImageInputRef.current) {
      mainImageInputRef.current.value = "";
    }
  };

  const handleResetImageScale = (target: ResizableTarget) => () => {
    setElementSizes((current) => ({
      ...current,
      [target]: templateElementSizes[target],
    }));
  };

  const handleClearScheduleField = (index: number, field: "time" | "content") => () => {
    setSchedules((current) => current.map((item, itemIndex) => (itemIndex === index ? { ...item, [field]: "" } : item)));
  };

  const handleClearSchedules = () => {
    setSchedules(createEmptySchedules());
    setOffDayLabel("");
  };

  const resetImageAdjustments = () => {
    setDragPositions((current) => ({
      ...current,
      mainImage: templateDragPositions.mainImage,
      credit: templateDragPositions.credit,
      logo: templateDragPositions.logo,
    }));
    setElementSizes(templateElementSizes);
    setActiveTarget((current) => (current === "mainImage" || current === "credit" || current === "logo" ? null : current));
  };

  const handleClearImages = () => {
    setLogoImageUrl("");
    setMainImageUrl("");
    resetImageInputValue(logoInputRef);
    resetImageInputValue(mainImageInputRef);
    imageDragDepthRef.current = { mainImage: 0, logo: 0 };
    setActiveImageDropTarget(null);
    resetImageAdjustments();
  };

  const handleClearComments = () => {
    setCommentTitle("");
    setCommentBody("");
    setDragPositions((current) => ({
      ...current,
      comment: templateDragPositions.comment,
    }));
    setActiveTarget((current) => (current === "comment" ? null : current));
  };

  const handleClearAttention = () => {
    setAttentionText("");
    setCredit("");
  };

  const handleClearAll = () => {
    const shouldClearAll = window.confirm("入力内容をすべてリセットします。よろしいですか？");
    if (!shouldClearAll) {
      return;
    }

    setStartDate(normalizeStartDateValue(""));
    handleClearSchedules();
    handleClearImages();
    handleClearComments();
    handleClearAttention();
    setSelectedTemplateId(DEFAULT_TEMPLATE.id);
    setSelectedThemeId(DEFAULT_THEME.id);
    // テンプレートも既定に戻すので、配置は上の各 handleClear* ではなく既定テンプレート基準で揃える
    setDragPositions(getTemplateDragPositions(DEFAULT_TEMPLATE.id));
    setElementSizes(getTemplateElementSizes(DEFAULT_TEMPLATE.id));
    setCopyMessage("");
    setExportImageUrl("");
    setIsExportModalOpen(false);
  };

  const handleTemplateChange = (templateId: string) => {
    const nextSelection = resolveTemplateThemeSelection({ templateId, themeId: selectedThemeId });
    setSelectedTemplateId(nextSelection.templateId);
    setSelectedThemeId(nextSelection.themeId);

    // レイアウトが変わるので、配置とサイズは切り替え先テンプレートの初期値に戻す
    if (nextSelection.templateId !== selectedTemplateId) {
      setDragPositions(getTemplateDragPositions(nextSelection.templateId));
      setElementSizes(getTemplateElementSizes(nextSelection.templateId));
    }
  };

  const handleStartTutorial = () => {
    tutorialDriverRef.current?.destroy();

    const steps = buildAppTourSteps();
    if (steps.length === 0) {
      return;
    }

    const tutorialDriver = driver({
      steps,
      showProgress: true,
      progressText: "{{current}} / {{total}}",
      smoothScroll: false,
      stagePadding: 12,
      overlayOpacity: 0.72,
      popoverClass: "schedule-tour-popover",
      nextBtnText: "次へ",
      prevBtnText: "戻る",
      doneBtnText: "完了",
      onDestroyed: () => {
        tutorialDriverRef.current = null;
      },
    });

    tutorialDriverRef.current = tutorialDriver;
    tutorialDriver.drive();
  };

  const handleExportImage = async () => {
    const exportContent = exportContentRef.current;
    if (!exportContent) {
      window.alert("画像生成の準備に失敗しました。");
      return;
    }

    setIsExporting(true);
    setCopyMessage("");
    setExportImageUrl("");
    setIsExportModalOpen(true);

    try {
      const dataUrl = await renderElementToDataUrl(exportContent);
      setExportImageUrl(dataUrl);
    } catch (error) {
      console.error(error);
      window.alert("画像生成に失敗しました。");
      setIsExportModalOpen(false);
    } finally {
      setIsExporting(false);
    }
  };

  const handleSaveImage = () => {
    if (!exportImageUrl) {
      return;
    }

    setCopyMessage("");
    const anchor = document.createElement("a");
    anchor.href = exportImageUrl;
    anchor.download = EXPORT_FILE_NAME;
    anchor.click();
  };

  const handleShareImage = async () => {
    if (!exportImageUrl) {
      return;
    }

    setCopyMessage("");

    try {
      const result = await shareScheduleImage({
        dataUrl: exportImageUrl,
        periodStartLabel,
        periodEndLabel,
        navigatorLike: navigator,
      });

      if (result === "unsupported") {
        window.alert("このブラウザでは画像共有に対応していません。");
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        return;
      }

      console.error(error);
      window.alert("共有に失敗しました。");
    }
  };

  const handleCopyImage = async () => {
    if (!exportImageUrl) {
      return;
    }

    try {
      const result = await copyImageDataUrlToClipboard({
        dataUrl: exportImageUrl,
        clipboardLike: navigator.clipboard,
        clipboardItemCtor: typeof ClipboardItem === "undefined" ? undefined : ClipboardItem,
      });

      if (result === "unsupported") {
        window.alert("このブラウザでは画像コピーに対応していません。");
        return;
      }

      setCopyMessage("クリップボードにコピーしました。");
    } catch (error) {
      console.error(error);
      window.alert("画像コピーに失敗しました。");
    }
  };

  const handleFillDummyData = () => {
    setCredit("Illustration by @XXXXXX");
    setCommentTitle("今週のひとこと");
    setCommentBody("夜配信多めの週です。週末はコラボがあります。");
    setAttentionText("※開始時間や内容は変更になる場合があります。");
    setSchedules([
      { time: "21:00", content: "雑談", isOff: false },
      { time: "20:00", content: "新作ゲーム実況", isOff: false },
      { time: "21:30", content: "歌枠", isOff: false },
      { time: "", content: "", isOff: true },
      { time: "22:00", content: "作業しながら雑談", isOff: false },
      { time: "20:00", content: "長編ゲーム続き", isOff: false },
      { time: "19:00", content: "コラボ配信", isOff: false },
    ]);
    setDragPositions(templateDragPositions);
    setElementSizes(templateElementSizes);
  };

  const previewThemeStyles = {
    ["--color-base" as string]: theme.base,
    ["--color-accent" as string]: theme.accent,
    ["--color-light" as string]: theme.light,
    ["--color-back" as string]: theme.back,
  };

  const scheduleTableItems = days.map((day, index) => {
    const item = schedules[index];
    const display = getScheduleDisplayValues(item, offDayLabel);

    return {
      key: day.dateLabel,
      dayNumber: day.dayNumber,
      weekLabel: day.weekLabel,
      time: display.time,
      content: display.content,
      isOff: item.isOff,
    };
  });

  const renderPreviewSurface = (isInteractive: boolean) => (
    <>
      <div
        className={`${isInteractive && mainImageUrl ? "drag-parts" : "parts"} mainImg ${isInteractive && activeTarget === "mainImage" ? "is-active" : ""}`.trim()}
        onPointerDown={isInteractive && mainImageUrl ? beginDrag("mainImage") : undefined}
        style={{
          transform: `translate(${dragPositions.mainImage.x}px, ${dragPositions.mainImage.y}px)`,
          width: `${elementSizes.mainImage}px`,
        }}
      >
        {mainImageUrl ? <img src={mainImageUrl} alt="イラスト" /> : null}
        {isInteractive && activeTarget === "mainImage" ? renderResizeHandles("mainImage") : null}
      </div>
      <div
        className={`${isInteractive ? "drag-parts" : "parts"} mainImgCopy ${credit ? "" : "hidden"} ${isInteractive && activeTarget === "credit" ? "is-active" : ""}`.trim()}
        onPointerDown={isInteractive && credit ? beginDrag("credit") : undefined}
        style={{ transform: `translate(${dragPositions.credit.x}px, ${dragPositions.credit.y}px)` }}
      >
        <span className="mainImgCopyTxt -fw-bold">{credit}</span>
      </div>
      <div
        className={`${isInteractive ? "drag-parts" : "parts"} comment-area ${commentBody ? "" : "hidden"} ${isInteractive && activeTarget === "comment" ? "is-active" : ""}`.trim()}
        onPointerDown={isInteractive ? beginDrag("comment") : undefined}
        style={{ transform: `translate(${dragPositions.comment.x}px, ${dragPositions.comment.y}px)` }}
      >
        <p className="comment-title -fw-black">{commentTitle}</p>
        <p className="comment -fw-bold">{commentBody}</p>
      </div>
      <div className="parts labelTitleLine">
        <TemplateLabelTitle
          periodStartLabel={periodStartLabel}
          periodEndLabel={periodEndLabel}
          theme={theme}
        />
      </div>
      <div className="bgParts">
        {TemplateBackgroundDecoration ? <TemplateBackgroundDecoration theme={theme} /> : null}
      </div>
      <TemplateScheduleTable
        items={scheduleTableItems}
        theme={theme}
        weekLabelSets={WEEK_LABEL_SETS}
      />
      <div
        className={`${isInteractive && logoImageUrl ? "drag-parts" : "parts"} logoImg ${isInteractive && activeTarget === "logo" ? "is-active" : ""}`.trim()}
        onPointerDown={isInteractive && logoImageUrl ? beginDrag("logo") : undefined}
        style={{
          transform: `translate(${dragPositions.logo.x}px, ${dragPositions.logo.y}px)`,
          width: `${elementSizes.logo}px`,
        }}
      >
        {logoImageUrl ? <img src={logoImageUrl} alt="ロゴ" /> : null}
        {isInteractive && activeTarget === "logo" ? renderResizeHandles("logo") : null}
      </div>
      <div className="parts attention">
        <p>{attentionText}</p>
      </div>
    </>
  );

  const renderImageSetting = ({
    label,
    imageUrl,
    target,
    inputRef,
    setter,
    thumbnailClassName,
  }: {
    label: string;
    imageUrl: string;
    target: ResizableTarget;
    inputRef: RefObject<HTMLInputElement>;
    setter: (value: string) => void;
    thumbnailClassName: string;
  }) => {
    const isDropActive = activeImageDropTarget === target;
    const inputChangeHandler = handleImageChange(setter, inputRef);

    return (
      <div className="image-setting-block">
        <label className="form-label">
          {label}
          <span className="font-bold text-pink-300">＊</span>
        </label>
        <div
          className={`image-setting-dropzone ${isDropActive ? "is-drag-active" : ""}`.trim()}
          data-image-target={target}
          onDragEnter={handleImageDragEnter(target)}
          onDragOver={handleImageDragOver(target)}
          onDragLeave={handleImageDragLeave(target)}
          onDrop={handleImageDrop(target, setter, inputRef)}
        >
          {imageUrl ? (
            <div className="image-setting-grid">
              <div className={`image-setting-thumb ${thumbnailClassName}`}>
                <img src={imageUrl} alt={`${label}のサムネイル`} className="image-setting-thumb-img" />
              </div>
              <div className="image-setting-actions">
                <label className="btn btn-secondary image-setting-file-btn cursor-pointer">
                  <LuImages size={14} />
                  <span className="truncate text-xs">変更する</span>
                  <input
                    ref={inputRef}
                    type="file"
                    accept={IMAGE_FILE_ACCEPT}
                    className="hidden"
                    onChange={inputChangeHandler}
                  />
                </label>
                <div className="image-setting-action-row">
                  <button
                    type="button"
                    onClick={handleResetImageScale(target)}
                    className="btn btn-secondary image-setting-scale-btn"
                    aria-label={`${label}のスケールをリセット`}
                    title={`${label}のスケールをリセット`}
                  >
                    <LuImageUpscale size={14} />
                    <span className="truncate">スケールをリセット</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleClearImage(target === "logo" ? "logo" : "mainImage")}
                    className="clear-icon-btn"
                    aria-label={`${label}をクリア`}
                    title={`${label}をクリア`}
                  >
                    <LuX size={16} />
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <label className="btn btn-secondary image-setting-file-btn cursor-pointer w-full">
              <LuImagePlus size={14} />
              <span className="truncate text-xs">ファイルを選択</span>
              <input
                ref={inputRef}
                type="file"
                accept={IMAGE_FILE_ACCEPT}
                className="hidden"
                onChange={inputChangeHandler}
              />
            </label>
          )}
          <p className="image-setting-dropnote">
            {isDropActive ? "ここにドロップして設定" : "ドラッグ＆ドロップでも設定できます"}
          </p>
        </div>
      </div>
    );
  };

  const previewCard = (
    <div id="previewCard" className="card preview-card" data-tour="preview">
      <div className="card-header">
        <LuLayers size={16} className="text-red-500 hidden lg:block" />
        <span className="card-title hidden lg:block">プレビュー</span>
        <div className="ml-auto flex gap-2">
          <button
            type="button"
            onClick={handleFillDummyData}
            className="btn btn-secondary py-1.5 px-3 text-xs"
          >
            <LuPencil size={12} />
            サンプル入力
          </button>
          <button
            type="button"
            onClick={handleClearAll}
            disabled={isExporting}
            className="btn btn-secondary px-3"
            aria-label="全体をクリア"
            title="全体をクリア"
          >
            <LuTrash2 size={16} />
            <span className="hidden sm:inline">全体をリセット</span>
          </button>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-gray-100">
        <div id="previewArea" className="w-full" ref={previewAreaRef}>
          <div
            id="previewContent"
            className="previewContent"
            data-design-template={selectedTemplateId}
            data-design-theme={selectedThemeId}
            style={{
              opacity: 1,
              transform: `translate(-50%, -50%) scale(${previewScale})`,
              ...previewThemeStyles,
            }}
          >
            {renderPreviewSurface(true)}
          </div>
        </div>
      </div>

      <p className="mt-3 text-center text-xs text-gray-500 hidden lg:block">
        <span className="text-pink-300 font-bold">＊</span> 画像やコメント要素は、ドラッグして位置を調整できます。画像は拡大縮小も可能です。
      </p>

      <div className="mt-3 text-right hidden lg:block">
        <button
          type="button"
          onClick={handleExportImage}
          disabled={isExporting}
          className="btn btn-primary"
        >
          <LuDownload size={16} />
          <span className="hidden sm:inline">{isExporting ? "生成中..." : "画像を生成"}</span>
          <span className="sm:hidden">{isExporting ? "..." : "生成"}</span>
        </button>
      </div>
    </div>
  );

  const previewSettingsCard = (
    <div className="card" data-tour="design">
      <div className="card-header">
        <LuPalette size={16} className="text-red-500" />
        <span className="card-title">デザイン＆配色</span>
      </div>
      <div>
        <div className="mb-3 flex items-center gap-2">
          <LuPanelsTopLeft size={14} className="text-gray-500" />
          <span className="section-title">デザインテンプレート</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {TEMPLATES.map((item, index) => (
            <label
              key={item.id}
              className={`theme-option ${item.id === selectedTemplateId ? "active" : ""}`}
            >
              <input
                type="radio"
                name="design-template"
                className="sr-only"
                checked={item.id === selectedTemplateId}
                onChange={() => handleTemplateChange(item.id)}
              />
              <div className="text-xs font-medium text-gray-700">{index + 1} {item.name}</div>
            </label>
          ))}
        </div>
      </div>

      <div className="mt-5 border-t border-gray-100 pt-5">
        <div className="mb-3 flex items-center gap-2">
          <LuPalette size={14} className="text-gray-500" />
          <span className="section-title">カラーテーマ</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {template.themes.map((item, index) => (
            <label
              key={item.id}
              className={`theme-option ${item.id === selectedThemeId ? "active" : ""}`}
            >
              <input
                type="radio"
                name="color-theme"
                className="sr-only"
                checked={item.id === selectedThemeId}
                onChange={() => setSelectedThemeId(item.id)}
              />
              <div className="mb-1.5 text-xs font-medium text-gray-700">{index + 1} {item.name}</div>
              <div className="flex overflow-hidden rounded">
                <span style={{ background: `rgb(${item.base})` }} className="h-5 w-5"></span>
                <span style={{ background: `rgb(${item.accent})` }} className="h-5 w-5"></span>
                <span style={{ background: `rgb(${item.light})` }} className="h-5 w-5"></span>
                <span style={{ background: `rgb(${item.back})` }} className="h-5 w-5"></span>
              </div>
            </label>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex h-svh flex-col overflow-hidden bg-gray-50 lg:block lg:min-h-screen lg:h-auto lg:overflow-visible">
      <Fonts />
      {/* Header */}
      <header className="sticky top-0 z-50 shrink-0 border-b border-gray-200 bg-white/95 backdrop-blur-sm">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6">
          <div className="w-full flex items-center gap-3">
            <div className="h-9 w-9 text-red-500">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 244.62 262.72">
                <g>
                  <g>
                      <path fill="currentColor" d="M83.18,106.24v-9.49h4.15v-9.3h-4.15v-6.94h-9.3v6.94h-12.96v9.3h12.96v8.74c-5.02-.19-9.18,1.12-12.46,3.91-3.41,2.85-5.15,6.76-5.15,11.78,0,3.66,1.24,6.76,3.78,9.49,2.48,2.67,5.52,3.97,9.11,3.97,4.03,0,7.38-1.36,10.04-4.15,2.67-2.79,3.97-6.2,3.97-10.23v-4.59c1.92.31,3.72.62,5.52,1.05l2.05-9.11c-2.36-.5-4.9-.93-7.56-1.36ZM73.88,120.25c0,1.43-.43,2.6-1.3,3.6-.93.99-2.05,1.49-3.41,1.49-.99,0-1.86-.43-2.54-1.3-.68-.87-1.05-1.8-1.05-2.85,0-4.28,2.48-6.39,7.44-6.39h.87v5.46Z"/>
                      <rect fill="currentColor" x="43.69" y="83.92" width="9.3" height="40.61"/>
                      <polygon fill="currentColor" points="192.08 91.74 185.38 98.18 199.89 113.31 206.58 106.86 192.08 91.74"/>
                      <path fill="currentColor" d="M166.59,117.28c-1.67-5.15-2.54-10.79-2.54-16.92,0-4.65.5-8.8,1.55-12.59l-8.93-2.54c-1.3,4.53-1.92,9.55-1.92,15.13,0,6.76.68,12.4,2.05,16.86,1.3,4.53,3.84,9.61,7.56,15.25l15.75-15.44-6.51-6.63-7,6.88Z"/>
                      <path fill="currentColor" d="M85.38,190.42v-9.3h-11.72v-4.15h-8.68v4.15h-17.67v9.3h17.67v3.1c-4.77-.31-8.68.81-11.72,3.47-3.16,2.79-4.77,6.51-4.77,11.22,0,3.41,1.18,6.39,3.53,8.87,2.29,2.48,5.15,3.72,8.56,3.72,1.55,0,3.16-.43,4.84-1.24,1.67-.81,2.91-1.86,3.72-3.1-.68,4.59-2.17,8.87-4.53,12.89l7.56,4.34c3.41-5.95,5.15-12.83,5.15-20.71,0-6.26-1.24-11.1-3.66-14.57v-8h11.72ZM64.98,207.34c0,1.3-.43,2.42-1.24,3.35-.81.93-1.86,1.43-3.16,1.43-.93,0-1.74-.43-2.42-1.24s-.99-1.67-.99-2.67c0-1.92.68-3.47,1.98-4.53,1.18-.99,2.79-1.49,4.77-1.49h1.05v5.15Z"/>
                      <path fill="currentColor" d="M193.29,177.4l-9.24.87c.31,3.35.62,6.57.81,9.61h-13.33v9.3h13.52c-.19,12.34-2.6,22.88-7.25,31.62l8.25,4.34c5.33-10.04,8.12-22.01,8.31-35.96h6.45v-9.3h-6.63c-.19-3.22-.43-6.7-.87-10.48Z"/>
                      <rect fill="currentColor" x="155.85" y="181.12" width="9.3" height="39.37"/>
                      <path fill="currentColor" d="M244.62,219.92s0-.02,0-.02l-1.16-167.62c0-18.2-14.8-33-33-33h-24.49V5c0-2.76-2.24-5-5-5s-5,2.24-5,5v14.27h-107.31V5c0-2.76-2.24-5-5-5s-5,2.24-5,5v14.27h-25.66C14.8,19.27,0,34.07,0,52.27v177.46c0,18.2,14.8,33,33.02,33l167.37-.71c3.58,0,7.04-1.49,9.5-4.07l30.79-28.38c.1-.09.2-.19.29-.29,2.34-2.54,3.63-5.84,3.65-9.32,0-.01,0-.02,0-.03,0,0,0,0,0-.01ZM213.53,214.93c-8.36,0-15.16,6.8-15.16,15.16v21.94l-73.56.31v-94.94h109.38l.4,57.53h-21.05ZM10,57.9h109.81v94.5H10V57.9ZM124.81,57.9h108.68l.66,94.5h-109.34V57.9ZM33,29.27h25.66v9.08c0,2.76,2.24,5,5,5s5-2.24,5-5v-9.08h107.31v9.08c0,2.76,2.24,5,5,5s5-2.24,5-5v-9.08h24.49c12.68,0,23,10.32,23,23.03v.59s-223.46,0-223.46,0v-.63c0-12.68,10.32-23,23-23ZM33,252.72c-12.68,0-23-10.32-23-23v-72.33h109.81v94.97l-86.81.37ZM208.37,245.74v-15.66c0-2.84,2.31-5.16,5.16-5.16h17.43l-22.58,20.82Z"/>
                  </g>
                </g>
              </svg>
            </div>
            <div>
              <h1 className="text-base font-bold text-gray-900">はいすけ</h1>
              <p className="hidden text-xs text-gray-500 sm:block">配信スケジュール画像作成ツール</p>
            </div>
            <div className="ml-auto flex items-center gap-2">
              {!isMobileLayout && 
                <button
                  type="button"
                  onClick={handleStartTutorial}
                  disabled={isExporting}
                  className="btn btn-secondary "
                  aria-label="チュートリアルを表示"
                  title="チュートリアルを表示"
                >
                  <LuCircleHelp size={16} />
                  使い方
                </button>
              }
              <button
                type="button"
                onClick={handleExportImage}
                disabled={isExporting}
                className="btn btn-primary"
              >
                <LuDownload size={16} />
                <span className="hidden sm:inline">{isExporting ? "生成中..." : "画像を生成"}</span>
                <span className="sm:hidden">{isExporting ? "..." : "生成"}</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {isMobileLayout && 
        <div className="mx-auto w-full max-w-7xl flex-shrink-0 px-4 pt-4 sm:px-6">
          {previewCard}
        </div>
      }

      <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden lg:overflow-visible">
        <main className="mx-auto mb-10 max-w-7xl overflow-x-hidden px-4 pt-4 sm:px-6 sm:overflow-x-visible lg:pt-6">
          <div className="flex flex-col gap-6 lg:flex-row-reverse lg:items-start">
            {!isMobileLayout ? (
              <div className="space-y-5 flex-1 lg:sticky lg:top-20">
                {previewCard}
                {previewSettingsCard}
              </div>
            ) : null}

            {isMobileLayout ? previewSettingsCard : null}

            {/* Left: Form Panel */}
            <div className="w-full space-y-5 lg:w-[420px] lg:flex-shrink-0">
            {/* Basic Settings Card */}
            <div className="card">
              <div className="card-header">
                <LuCalendarRange size={16} className="text-red-500" />
                <span className="card-title">期間</span>
              </div>
              <div className="space-y-4">
                <div data-tour="period">
                  <label htmlFor="form04" className="form-label">開始日</label>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                    <div className="field-with-inline-clear field-with-inline-clear-date w-full sm:w-auto">
                      <input
                        type="date"
                        id="form04"
                        className="form-input w-full sm:w-auto"
                        value={startDate}
                        onChange={(event) => setStartDate(normalizeStartDateValue(event.target.value))}
                      />
                      {startDate ? (
                        <button
                          type="button"
                          className="inline-clear-btn"
                          onClick={() => setStartDate(normalizeStartDateValue(""))}
                          aria-label="開始日を今週の月曜に戻す"
                          title="開始日を今週の月曜に戻す"
                        >
                          <LuX size={14} />
                        </button>
                      ) : null}
                    </div>
                    <span className="text-sm text-gray-500 sm:whitespace-nowrap">
                      {days[0].dateLabel.replaceAll("-", "/")} - {days[6].dateLabel.replaceAll("-", "/")}
                    </span>
                  </div>
                  <p className="mt-2 text-xs text-gray-400">選択した週の月曜に自動で揃えます</p>
                </div>
              </div>
            </div>

            {/* Schedule Card */}
            <div className="card">
              <div className="card-header">
                <LuCalendar size={16} className="text-red-500" />
                <span className="card-title">週間スケジュール</span>
                <button
                  type="button"
                  onClick={handleClearSchedules}
                  className="btn btn-secondary ml-auto px-3 py-1.5 text-xs"
                  aria-label="週間スケジュールを一括クリア"
                  title="週間スケジュールを一括クリア"
                >
                  <LuTrash2 size={12} />
                  一括リセット
                </button>
              </div>
              <div className="space-y-4">
                <div data-tour="schedule02">
                  <label htmlFor="form05_off_day" className="form-label">休日の文言（任意）</label>
                  <div className="field-with-inline-clear">
                    <input
                      type="text"
                      id="form05_off_day"
                      className="form-input"
                      placeholder={OFF_DAY_LABEL}
                      value={offDayLabel}
                      onChange={(event) => setOffDayLabel(event.target.value)}
                    />
                    {offDayLabel ? (
                      <button
                        type="button"
                        className="inline-clear-btn"
                        onClick={() => setOffDayLabel("")}
                        aria-label="おやすみの文言をクリア"
                        title="おやすみの文言をクリア"
                      >
                        <LuX size={14} />
                      </button>
                    ) : null}
                  </div>
                  <p className="mt-2 text-xs text-gray-400">未入力の場合は「{OFF_DAY_LABEL}」を使います</p>
                </div>
                <div className="space-y-2">
                  {days.map((day, index) => (
                    <div
                      key={`form-${day.dateLabel}`}
                      className={`schedule-row ${schedules[index].isOff ? "is-off" : ""}`.trim()}
                       data-tour={index===0 && "schedule01"}>
                      <div className="schedule-meta">
                        <div className="schedule-day">{day.dayNumber}</div>
                        <div className="schedule-week">{day.weekLabel}</div>
                      </div>
                      <div className="schedule-fields">
                        <label className={`schedule-off-toggle ${schedules[index].isOff ? "is-checked" : ""}`.trim()}>
                          <input
                            type="checkbox"
                            aria-label="休日"
                            checked={schedules[index].isOff}
                            onChange={handleScheduleOffChange(index)}
                          />
                          <span>{resolveOffDayLabel(offDayLabel)}</span>
                        </label>
                        <div className="schedule-inputs">
                          <div className="field-with-inline-clear schedule-time-field">
                            <input
                              type="time"
                              className="form-input schedule-time-input"
                              placeholder="12:00"
                              aria-label={`${day.weekLabel}曜日の配信時間`}
                              value={schedules[index].time}
                              onFocus={handleScheduleTimeFocus(index)}
                              onChange={handleScheduleTextChange(index, "time")}
                              disabled={schedules[index].isOff}
                              step={60}
                            />
                            {schedules[index].time ? (
                              <button
                                type="button"
                                className="inline-clear-btn"
                                onClick={handleClearScheduleField(index, "time")}
                                aria-label={`${day.dayNumber}${day.weekLabel}の時刻をクリア`}
                                title={`${day.dayNumber}${day.weekLabel}の時刻をクリア`}
                              >
                                <LuX size={14} />
                              </button>
                            ) : null}
                          </div>
                          <div className="field-with-inline-clear field-with-inline-clear-textarea schedule-content-field">
                            <textarea
                              ref={(element) => {
                                scheduleContentRefs.current[index] = element;
                              }}
                              className="form-input schedule-content-input"
                              placeholder="配信内容を入力..."
                              value={schedules[index].content}
                              onChange={handleScheduleTextChange(index, "content")}
                              onInput={(event) => resizeScheduleContentTextarea(event.currentTarget)}
                              disabled={schedules[index].isOff}
                              rows={1}
                            />
                            {schedules[index].content ? (
                              <button
                                type="button"
                                className="inline-clear-btn inline-clear-btn-textarea"
                                onClick={handleClearScheduleField(index, "content")}
                                aria-label={`${day.dayNumber}${day.weekLabel}の内容をクリア`}
                                title={`${day.dayNumber}${day.weekLabel}の内容をクリア`}
                              >
                                <LuX size={14} />
                              </button>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Images Card */}
            <div className="card">
              <div className="card-header">
                <LuImagePlus size={16} className="text-red-500" />
                <span className="card-title">画像設定</span>
                <div className="ml-auto flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleClearImages}
                    className="btn btn-secondary px-3 py-1.5 text-xs"
                    aria-label="画像設定を一括クリア"
                    title="画像設定を一括クリア"
                  >
                    <LuTrash2 size={12} />
                    一括リセット
                  </button>
                </div>
              </div>
              <div className="space-y-4" data-tour="images">
                {renderImageSetting({
                  label: "ロゴ画像（任意）",
                  imageUrl: logoImageUrl,
                  target: "logo",
                  inputRef: logoInputRef,
                  setter: setLogoImageUrl,
                  thumbnailClassName: "image-setting-thumb-logo",
                })}
                {renderImageSetting({
                  label: "メインイラスト（任意）",
                  imageUrl: mainImageUrl,
                  target: "mainImage",
                  inputRef: mainImageInputRef,
                  setter: setMainImageUrl,
                  thumbnailClassName: "image-setting-thumb-main",
                })}
              </div>
            </div>

            <div className="space-y-5" data-tour="andmore">

              {/* Comment Card */}
              <div className="card">
                <div className="card-header">
                  <LuLightbulb size={16} className="text-red-500" />
                  <span className="card-title">注釈・クレジット</span>
                  <button
                    type="button"
                    onClick={handleClearAttention}
                    className="btn btn-secondary ml-auto px-3 py-1.5 text-xs"
                    aria-label="コメント・注釈を一括クリア"
                    title="コメント・注釈を一括クリア"
                  >
                    <LuTrash2 size={12} />
                    一括リセット
                  </button>
                </div>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="form07" className="form-label">注釈テキスト（任意）</label>
                    <div className="field-with-inline-clear">
                      <input
                        type="text"
                        id="form07"
                        className="form-input"
                        placeholder="※予定は変更になる場合があります。"
                        value={attentionText}
                        onChange={(event) => setAttentionText(event.target.value)}
                      />
                      {attentionText ? (
                        <button
                          type="button"
                          className="inline-clear-btn"
                          onClick={() => setAttentionText("")}
                          aria-label="注釈テキストをクリア"
                          title="注釈テキストをクリア"
                        >
                          <LuX size={14} />
                        </button>
                      ) : null}
                    </div>
                  </div>
                  <div>
                    <label htmlFor="form03" className="form-label">クレジット表記（任意）<span className="text-pink-300 font-bold">＊</span></label>
                    <div className="field-with-inline-clear">
                      <input
                        type="text"
                        id="form03"
                        className="form-input"
                        placeholder="Art by ..."
                        value={credit}
                        onChange={(event) => setCredit(event.target.value)}
                      />
                      {credit ? (
                        <button
                          type="button"
                          className="inline-clear-btn"
                          onClick={() => setCredit("")}
                          aria-label="クレジット表記をクリア"
                          title="クレジット表記をクリア"
                        >
                          <LuX size={14} />
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>

              {/* Comment Card */}
              <div className="card">
                <div className="card-header">
                  <LuMessageSquare size={16} className="text-red-500" />
                  <span className="card-title">コメント <span className="text-pink-300 font-bold">＊</span></span>
                  <button
                    type="button"
                    onClick={handleClearComments}
                    className="btn btn-secondary ml-auto px-3 py-1.5 text-xs"
                    aria-label="コメント・注釈を一括クリア"
                    title="コメント・注釈を一括クリア"
                  >
                    <LuTrash2 size={12} />
                    一括リセット
                  </button>
                </div>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="form06_label" className="form-label">一言コメントの見出し（任意）</label>
                    <div className="field-with-inline-clear">
                      <input
                        type="text"
                        id="form06_label"
                        className="form-input"
                        placeholder="今週のひとこと"
                        value={commentTitle}
                        onChange={(event) => setCommentTitle(event.target.value)}
                      />
                      {commentTitle ? (
                        <button
                          type="button"
                          className="inline-clear-btn"
                          onClick={() => setCommentTitle("")}
                          aria-label="一言コメントの見出しをクリア"
                          title="一言コメントの見出しをクリア"
                        >
                          <LuX size={14} />
                        </button>
                      ) : null}
                    </div>
                  </div>
                  <div>
                    <label htmlFor="form06" className="form-label">コメント本文（任意）</label>
                    <div className="field-with-inline-clear field-with-inline-clear-textarea">
                      <textarea
                        id="form06"
                        className="form-input min-h-[80px] resize-none"
                        placeholder="コメント本文を入力。改行可能です。"
                        value={commentBody}
                        onChange={(event) => setCommentBody(event.target.value)}
                      />
                      {commentBody ? (
                        <button
                          type="button"
                          className="inline-clear-btn inline-clear-btn-textarea"
                          onClick={() => setCommentBody("")}
                          aria-label="コメント本文をクリア"
                          title="コメント本文をクリア"
                        >
                          <LuX size={14} />
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            </div>

          </div>
        </main>

        <section className="mx-auto mb-10 max-w-7xl px-4 pt-6 sm:px-6">
          <div className="card">
            <div className="card-header">
              <LuHistory size={16} className="text-red-500" />
              <span className="card-title">更新履歴</span>
            </div>
            <div className="space-y-2">
              {UPDATE_HISTORY_ITEMS.slice(0, MAX_VISIBLE_UPDATE_HISTORY).map((item) => (
                <article key={`${item.date}-${item.title}`} className="flex items-start gap-3">
                  <p className="text-xs font-semibold tracking-[0.18em] text-gray-400">{item.date}</p>
                  <h3 className="text-xs text-gray-400">{item.title}</h3>
                </article>
              ))}
              <p className="text-xs">
                <button
                  type="button"
                  onClick={() => setIsUpdateHistoryModalOpen(true)}
                  className="underline font-medium text-red-500 transition-all duration-200 hover:text-red-300"
                >
                  MORE
                </button>
              </p>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="mt-4 border-t border-gray-200 bg-white">
          <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6">
            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-gray-500">
              <span>制作： いにし

                <a href="https://torica.booth.pm/" target="_blank"
                  rel="noopener noreferrer"
                  className="px-0.5 pl-3 underline font-medium text-red-500 transition-all duration-200 hover:text-red-300">
                  BOOTH</a>
              ｜
                <a href="https://x.com/torica124nishi" target="_blank"
                  rel="noopener noreferrer"
                  className="px-0.5 underline font-medium text-red-500 transition-all duration-200 hover:text-red-300">
                  X(Twitter)</a>
              ｜
                <a href="https://torica.me/" target="_blank"
                  rel="noopener noreferrer"
                  className="px-0.5 underline font-medium text-red-500 transition-all duration-200 hover:text-red-300">
                  サイト
                </a>
              </span>
            </div>
          </div>
        </footer>
      </div>

      <div className="export-preview-root" aria-hidden="true">
        <div
          ref={exportContentRef}
          className="previewContent"
          data-design-template={selectedTemplateId}
          data-design-theme={selectedThemeId}
          style={previewThemeStyles}
        >
          {renderPreviewSurface(false)}
        </div>
      </div>

      {/* Floating Action Buttons */}
      <div className="fixed right-0 bottom-4 z-[9990] flex flex-col gap-3">
        <button
          type="button"
          onClick={() => setIsAboutModalOpen(true)}
          className="group flex items-center flex-col gap-2 rounded-l-xl bg-white px-2 py-3 sm:pb-4 text-sm font-medium text-gray-700 shadow-md ring-1 ring-gray-100 transition-all duration-200 hover:-translate-y-0.5 hover:text-red-500 hover:shadow-lg"
          data-tour="about"
        >
          <span className="flex h-5 w-5 lg:h-7 lg:w-7 flex-shrink-0 items-center justify-center rounded-full bg-red-50 text-red-500 transition-colors group-hover:bg-red-100">
            <LuInfo size={16} />
          </span>
          <span className="hidden sm:inline [writing-mode:vertical-rl]">このツールについて</span>
        </button>
        <a
          href="https://docs.google.com/forms/d/e/1FAIpQLScDnLogiT0Z0ReGkJWr1DpuoIjPOmPhbkC1PaexmEBcPcETZA/viewform?usp=publish-editor"
          target="_blank"
          rel="noopener noreferrer"
          className="group flex items-center flex-col gap-2 rounded-l-xl bg-white px-2 py-3 sm:pb-4 text-sm font-medium text-gray-700 shadow-md ring-1 ring-gray-100 transition-all duration-200 hover:-translate-y-0.5 hover:text-red-500 hover:shadow-lg"
        >
          <span className="flex  h-5 w-5 lg:h-7 lg:w-7 flex-shrink-0 items-center justify-center rounded-full bg-red-50 text-red-500 transition-colors group-hover:bg-red-100">
            <LuContactRound size={16} />
          </span>
          <span className="hidden sm:inline [writing-mode:vertical-rl]">不具合・要望報告</span>
        </a>
        <a
          href="https://ofuse.me/torica124nishi"
          target="_blank"
          rel="noopener noreferrer"
          className="group flex items-center flex-col gap-2 rounded-l-xl bg-white px-2 py-3 sm:pb-4 text-sm font-medium text-gray-700 shadow-md ring-1 ring-gray-100 transition-all duration-200 hover:-translate-y-0.5 hover:text-red-500 hover:shadow-lg"
        >
          <span className="flex  h-5 w-5 lg:h-7 lg:w-7 flex-shrink-0 items-center justify-center rounded-full bg-red-50 text-red-500 transition-colors group-hover:bg-red-100">
            <OfuseIcon className="h-4 w-4" color="currentColor" secondaryColor="#fef2f2" />
          </span>
          <span className="hidden sm:inline [writing-mode:vertical-rl]">開発を支援</span>
        </a>
      </div>

      {/* About Modal */}
      {isAboutModalOpen && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
          onClick={() => setIsAboutModalOpen(false)}
        >
          <div
            className="relative flex max-h-[calc(100svh-2rem)] w-full max-w-2xl animate-fade-in flex-col rounded-2xl bg-white p-5 shadow-2xl sm:p-6"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setIsAboutModalOpen(false)}
              className="absolute right-3 top-3 flex h-10 w-10 items-center justify-center rounded-full bg-red-500 text-white shadow-lg transition-colors hover:bg-red-600 sm:-right-3 sm:-top-3"
              aria-label="モーダルを閉じる"
            >
              <LuX size={20} />
            </button>

            <h2 className="mb-4 pr-12 flex justify-center items-center gap-2 text-center text-lg font-bold text-gray-900 sm:pr-0">
              <span className="text-red-500">
                <LuInfo size={16} />
              </span>
              このツールについて
            </h2>

            <div className="-mr-2 overflow-y-auto pr-2">
              <dl className="divide-y divide-gray-100">
                {ABOUT_FAQ.map((item) => (
                  <div key={item.q} className="py-4 first:pt-0 last:pb-0">
                    <dt className="mb-1.5 font-bold text-red-500">Q. {item.q}</dt>
                    <dd className="text-sm leading-relaxed text-gray-700">
                      {item.a}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </div>
      )}

      {/* Update History Modal */}
      {isUpdateHistoryModalOpen && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
          onClick={() => setIsUpdateHistoryModalOpen(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="update-history-modal-title"
            className="relative flex max-h-[calc(100svh-2rem)] w-full max-w-2xl animate-fade-in flex-col rounded-2xl bg-white p-5 shadow-2xl sm:p-6"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setIsUpdateHistoryModalOpen(false)}
              className="absolute right-3 top-3 flex h-10 w-10 items-center justify-center rounded-full bg-red-500 text-white shadow-lg transition-colors hover:bg-red-600 sm:-right-3 sm:-top-3"
              aria-label="モーダルを閉じる"
            >
              <LuX size={20} />
            </button>

            <h2
              id="update-history-modal-title"
              className="mb-4 pr-12 flex justify-center items-center gap-2 text-center text-lg font-bold text-gray-900 sm:pr-0"
            >
              <span className="text-red-500">
                <LuHistory size={16} />
              </span>
              更新履歴
            </h2>

            <div className="-mr-2 overflow-y-auto pr-2">
              <dl className="divide-y divide-gray-100">
                {UPDATE_HISTORY_ITEMS.map((item) => (
                  <div key={`${item.date}-${item.title}`} className="py-4 first:pt-0 last:pb-0">
                    <dt className="mb-1.5 text-xs font-semibold tracking-[0.18em] text-gray-400">{item.date}</dt>
                    <dd className="text-sm leading-relaxed text-gray-700">{item.title}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      {isExportModalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
          <div className="relative max-h-[calc(100vh-2rem)] w-full max-w-4xl animate-fade-in rounded-2xl bg-white p-5 shadow-2xl sm:p-6">
            <button
              type="button"
              onClick={() => setIsExportModalOpen(false)}
              className="absolute right-3 top-3 flex h-10 w-10 items-center justify-center rounded-full bg-red-500 text-white shadow-lg transition-colors hover:bg-red-600 sm:-right-3 sm:-top-3"
              aria-label="モーダルを閉じる"
            >
              <LuX size={20} />
            </button>

            <h2 className="mb-4 pr-12 text-center text-lg font-bold text-gray-900 sm:pr-0">週間スケジュールができました🙌</h2>

            <div className="modal-img relative overflow-auto rounded-lg border border-gray-200 bg-gray-50">
              <div id="exportImage"></div>
              {exportImageUrl ? (
                <img src={exportImageUrl} alt="生成されたスケジュール画像" id="exportImagePNG" className="block w-full" />
              ) : (
                <div className="flex aspect-video w-full items-center justify-center bg-gray-50 text-sm text-gray-500">
                  {isExporting ? (
                    <div className="flex items-center gap-2">
                      <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-red-500"></div>
                      <span>画像を生成しています...</span>
                    </div>
                  ) : (
                    "生成画像がここに表示されます"
                  )}
                </div>
              )}
            </div>

            <div className="mt-5 flex flex-col justify-center gap-3 sm:flex-row">
              <button
                type="button"
                onClick={handleSaveImage}
                disabled={!exportImageUrl}
                className="btn btn-primary w-full sm:w-auto"
              >
                <LuDownload size={16} />
                保存
              </button>
              <button
                type="button"
                onClick={handleCopyImage}
                disabled={!exportImageUrl}
                className="btn btn-secondary w-full sm:w-auto"
              >
                <LuCopy size={16} />
                コピー
              </button>
              <button
                type="button"
                onClick={handleShareImage}
                disabled={!exportImageUrl}
                className="btn btn-secondary w-full sm:w-auto"
              >
                <LuShare2 size={16} />
                共有
              </button>
            </div>

            {copyMessage && (
              <p className="mt-3 flex items-center justify-center gap-1 text-center text-sm text-red-500">
                <LuCheck size={14} />
                {copyMessage}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
